/**
 * VehicleInfoSection
 *
 * Collapsible vehicle list used in the Review flow. Mirrors the visual
 * language of the Client/Insurance sections: white cards, bold labels, and
 * values beneath. Uses `CompositeItem`/`InlineEditableRow` rows and persists
 * edits via the coverage update mutation.
 */
import React, { useMemo, useState } from 'react';

import { US_STATE_OPTIONS } from '@/components/ui/address-autocomplete/constants';
import { useUpdateCoverageMutation } from '@/services/api/coverageService';
import { CoverageSchema, VehicleDetail } from '@/services/types/coverage-types';

/** State options for the license plate state dropdown */
const LICENSE_PLATE_STATE_OPTIONS = [
  ...US_STATE_OPTIONS.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` })),
  { value: 'OTHER', label: 'Other' },
];

import { CompositeItem } from '../review/CompositeItem';
import { DisclosureSection } from './DisclosureSection';
import { FieldSection } from './FieldSection';

interface VehicleInfoSectionProps {
  matterId: string;
  coverage: CoverageSchema;
  onHoverField?: (key: string | null) => void;
}

export const VehicleInfoSection: React.FC<VehicleInfoSectionProps> = ({
  matterId,
  coverage,
  onHoverField,
}) => {
  const [updateCoverage] = useUpdateCoverageMutation();

  const initialVehicles: VehicleDetail[] = useMemo(
    () => (coverage.vehicles || []) as VehicleDetail[],
    [coverage.vehicles],
  );

  const [vehiclesState, setVehiclesState] =
    useState<VehicleDetail[]>(initialVehicles);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const removeVehicle = (index: number) => {
    setVehiclesState((prev) => prev.filter((_, i) => i !== index));
  };

  const saveVehicles = async (nextVehicles: VehicleDetail[]) => {
    try {
      await updateCoverage({
        matterId,
        coverageId: coverage.id,
        coverage: { vehicles: nextVehicles as any },
      }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update vehicles', err);
    }
  };

  const renderVehicle = (v: VehicleDetail, i: number) => {
    const title = [v.year, [v.make, v.model].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(' ');

    return (
      <CompositeItem
        key={`veh-${i}`}
        title={title || 'Vehicle'}
        titleHoverKey={`vehicle:${i}:title`}
        onHover={onHoverField}
        actions={
          <button
            type="button"
            className="text-xs text-gray-700 px-2 py-1 rounded hover:bg-gray-50"
            onClick={() => removeVehicle(i)}
          >
            Remove
          </button>
        }
        rows={
          [
            {
              id: `vehicle:${i}:year`,
              label: 'Year',
              value: String((v as any).year ?? ''),
              editable: true,
              inlineEdit: true,
              required: true,
              isMono: true,
              hoverKey: `vehicle:${i}:year`,
              onChange: (next: string) =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) =>
                    idx === i ? { ...vv, year: next as any } : vv,
                  ),
                ),
              onSave: async () => {
                const nextVehicles = vehiclesState.map((vv, idx) =>
                  idx === i ? { ...vv } : vv,
                );
                await saveVehicles(nextVehicles);
              },
              onCancel: () =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) => (idx === i ? { ...v } : vv)),
                ),
            },
            {
              id: `vehicle:${i}:make`,
              label: 'Make',
              value: String((v as any).make ?? ''),
              editable: true,
              inlineEdit: true,
              required: true,
              hoverKey: `vehicle:${i}:make`,
              onChange: (next: string) =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) =>
                    idx === i ? { ...vv, make: next as any } : vv,
                  ),
                ),
              onSave: async () => {
                const nextVehicles = vehiclesState.map((vv, idx) =>
                  idx === i ? { ...vv } : vv,
                );
                await saveVehicles(nextVehicles);
              },
              onCancel: () =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) => (idx === i ? { ...v } : vv)),
                ),
            },
            {
              id: `vehicle:${i}:model`,
              label: 'Model',
              value: String((v as any).model ?? ''),
              editable: true,
              inlineEdit: true,
              required: true,
              hoverKey: `vehicle:${i}:model`,
              onChange: (next: string) =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) =>
                    idx === i ? { ...vv, model: next as any } : vv,
                  ),
                ),
              onSave: async () => {
                const nextVehicles = vehiclesState.map((vv, idx) =>
                  idx === i ? { ...vv } : vv,
                );
                await saveVehicles(nextVehicles);
              },
              onCancel: () =>
                setVehiclesState((prev) =>
                  prev.map((vv, idx) => (idx === i ? { ...v } : vv)),
                ),
            },
            v.color
              ? {
                  id: `vehicle:${i}:color`,
                  label: 'Color',
                  value: String(v.color),
                  editable: true,
                  inlineEdit: true,
                  required: false,
                  hoverKey: `vehicle:${i}:color`,
                  onChange: (next: string) =>
                    setVehiclesState((prev) =>
                      prev.map((vv, idx) =>
                        idx === i ? { ...vv, color: next as any } : vv,
                      ),
                    ),
                  onSave: async () => {
                    const nextVehicles = vehiclesState.map((vv, idx) =>
                      idx === i ? { ...vv } : vv,
                    );
                    await saveVehicles(nextVehicles);
                  },
                  onCancel: () =>
                    setVehiclesState((prev) =>
                      prev.map((vv, idx) => (idx === i ? { ...v } : vv)),
                    ),
                }
              : null,
            v.license_plate
              ? {
                  id: `vehicle:${i}:plate`,
                  label: 'Plate',
                  value: String(v.license_plate),
                  isMono: true,
                  editable: true,
                  inlineEdit: true,
                  required: true,
                  hoverKey: `vehicle:${i}:plate`,
                  onChange: (next: string) =>
                    setVehiclesState((prev) =>
                      prev.map((vv, idx) =>
                        idx === i ? { ...vv, license_plate: next as any } : vv,
                      ),
                    ),
                  onSave: async () => {
                    const nextVehicles = vehiclesState.map((vv, idx) =>
                      idx === i ? { ...vv } : vv,
                    );
                    await saveVehicles(nextVehicles);
                  },
                  onCancel: () =>
                    setVehiclesState((prev) =>
                      prev.map((vv, idx) => (idx === i ? { ...v } : vv)),
                    ),
                }
              : null,
            v.license_plate_state
              ? {
                  id: `vehicle:${i}:plate_state`,
                  label: 'Plate State',
                  value: LICENSE_PLATE_STATE_OPTIONS.find((o) => o.value === v.license_plate_state)?.label || v.license_plate_state,
                  editable: false,
                  inlineEdit: false,
                  required: false,
                  hoverKey: `vehicle:${i}:plate_state`,
                }
              : null,
          ].filter(Boolean) as any
        }
      />
    );
  };

  const vehiclesSummary = undefined;

  return (
    <DisclosureSection
      title="Vehicle Information"
      isOpen={isOpen}
      onToggle={() => setIsOpen((v) => !v)}
    >
      <FieldSection className="border-0">
        <div className="space-y-3 px-3 py-2">
          {vehiclesState.length === 0
            ? null
            : vehiclesState.map((v, i) => renderVehicle(v, i))}
        </div>
      </FieldSection>
    </DisclosureSection>
  );
};
